import React, { FC, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { ParsedQuery } from 'query-string';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { pagingInfoMock } from '../../__mocks__/surveysData';
import { EReportType } from '../../constans/report';
import { useReportsSetup } from '../../hooks';
import { useFilters } from '../../hooks/useFilters';
import { IReportFilters } from '../../services/reports';
import { IPagingInfo, IReportsSetupMember } from '../../services/surveys';
import AddParticipantNewForm from '../AddParticipantNewForm/AddParticipantNewForm';
import DeleteReportMemberModal from '../DeleteReportMemberModal/DeleteReportMemberModal';
import NoData from '../NoData/NoData';
import Pagination from '../Pagination/Pagination';
import RowsPerPage from '../RowsPerPage/RowsPerPage';
import ReportsSetupMemberTile from './ReportsSetupMemberTile/ReportsSetupMemberTile';
import {
  TableFooter,
  TableTitle,
  Container,
  Header,
  Data,
  NameColumn,
  DetailsCont,
  TeamColumn,
  RowsPerPageContainer,
  RoleColumn,
} from './ReportsSetupParticipants.styles';

interface IReportsSetupParticipants {
  groupId: string;
  activeForm: boolean;
  setActiveForm: (status: boolean) => void;
  type: string;
  updateFullTable: () => Promise<void>;
  hasSearchResults?: boolean;
  isEffectSurvey: boolean;
}

const ReportsSetupParticipants: FC<IReportsSetupParticipants> = ({
  groupId,
  setActiveForm,
  activeForm,
  type,
  updateFullTable,
  hasSearchResults,
  isEffectSurvey,
}) => {
  const { surveyId } = useParams();
  const { t } = useTranslation();
  const [activeModal, setActiveModal] = useState<boolean>(false);
  const [currentMemberId, setCurrentMemberId] = useState<string | null>(null);
  const [members, setMembers] = useState<IReportsSetupMember[]>([]);
  const [pagingInfo, setPagingInfo] = useState<IPagingInfo>(pagingInfoMock);
  const { deleteReportMember, downloadMembers } = useReportsSetup();
  const { applyNestedFilter, currentFilters, getNestedFilters } = useFilters();
  const reportsAllFilters = getNestedFilters('reports');
  const thisReportFilter: IReportFilters = reportsAllFilters[groupId] || {};
  const { perPage, pageNo } = thisReportFilter;
  const { search } = currentFilters;
  const noDataInfoMessage =
    type === 'team'
      ? t('viewSurvey.reportsSetup.participants.noDataInfo')
      : t('viewSurvey.reportsSetup.participants.noDataExecutiveInfo');

  const updateMembersList = async (overrideFilters: ParsedQuery = {}) => {
    const results = await downloadMembers(surveyId || '0', groupId, {
      ...thisReportFilter,
      ...overrideFilters,
    });
    setMembers(results.items);
    setPagingInfo(results.pagingInfo);
  };

  const updateBothLists = async ({ memberFilters }: { memberFilters?: any } = {}) => {
    await updateMembersList(memberFilters);
    await updateFullTable();
  };

  const handleDeleteModal = (id: string | null) => {
    setCurrentMemberId(id);
    setActiveModal(activeModal => !activeModal);
  };

  const handleClose = () => {
    handleDeleteModal(null);
  };

  const handleConfirm = async () => {
    if (!currentMemberId) return;

    const pageNumber = members?.length !== 1 ? pagingInfo.pageNo : '1';

    try {
      await deleteReportMember(groupId, currentMemberId);
      await updateBothLists({ memberFilters: { pageNo: pageNumber } });

      if (pageNumber !== pagingInfo.pageNo) {
        applyNestedFilter({ pageNo: pageNumber }, groupId, 'reports');
      }
    } finally {
      handleDeleteModal(null);
    }
  };

  useEffect(() => {
    (async () => {
      await updateMembersList({ search: hasSearchResults ? search : null });
    })();
  }, [groupId, search, surveyId, pageNo, perPage, hasSearchResults]);

  const renderMembers = useMemo(() => {
    return members?.map(member => (
      <ReportsSetupMemberTile
        key={member.id}
        member={member}
        type={type}
        deleteMember={handleDeleteModal}
        isEffectSurvey={isEffectSurvey}
      />
    ));
  }, [members, type]);

  return (
    <Container id="Container">
      <TableTitle id="TableTitle">
        {type === 'team'
          ? t('viewSurvey.reportsSetup.participants.participants')
          : t('viewSurvey.reportsSetup.participants.TeamReports')}
      </TableTitle>
      <Header id="Header">
        <NameColumn id="NameColumn">{t('viewSurvey.reportsSetup.participants.name')}</NameColumn>
        {isEffectSurvey ? (
          <RoleColumn id="RoleColumn">{t('viewSurvey.reportsSetup.participants.role')}</RoleColumn>
        ) : (
          <DetailsCont id="DetailsCont">
            <TeamColumn id="TeamColumn">
              {type === 'team'
                ? t('viewSurvey.reportsSetup.participants.team')
                : t('viewSurvey.reportsSetup.participants.participants')}
            </TeamColumn>
            <Data id="Data">
              {type === 'team' && t('viewSurvey.reportsSetup.participants.leader')}
            </Data>
          </DetailsCont>
        )}
      </Header>
      <DeleteReportMemberModal
        active={activeModal}
        type={type as EReportType}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <AddParticipantNewForm
        type={type}
        setActive={setActiveForm}
        active={activeForm}
        groupId={groupId}
        updateMembersList={updateBothLists}
      />
      <>{_.isEmpty(members) ? <NoData message={noDataInfoMessage} /> : renderMembers}</>
      <TableFooter id="TableFooter">
        <RowsPerPageContainer>
          <RowsPerPage
            itemsPerPage={Number(pagingInfo.itemsPerPage)}
            pagesCount={Number(pagingInfo.totalPageCount)}
            menu={['5', '10', '25', '50', '75', '100']}
            customApplyFilters={filters => applyNestedFilter(filters, groupId, 'reports')}
          />
        </RowsPerPageContainer>
        <Pagination
          activePage={Number(pagingInfo.pageNo)}
          totalPageCount={Number(pagingInfo.totalPageCount)}
          applyFilters={(pageNo: number) =>
            applyNestedFilter({ pageNo: String(pageNo) }, groupId, 'reports')
          }
        />
      </TableFooter>
    </Container>
  );
};

export default ReportsSetupParticipants;
